import { CircleAlert, Pencil, RefreshCw } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import type { Language, ReportSection, ReportSectionStatus } from "~/api/model";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { LoadingSpinner } from "~/components/Icons";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useLazyRender } from "~/hooks/useLazyRender";
import { trackEvent } from "~/utils/analytics";
import {
  BatesLink,
  SectionLink,
  TERMS_PROSE_CLASS,
  ensureBoldHeadingSpacing,
  ensureMarkdownNewlines,
  getTermsAndSectionsMarkdownComponents,
  highlightTermsAndSectionsInMarkdown,
  normalizeLiteralNewlines,
  unwrapAllCodeBlocks,
} from "~/utils/markdownTerms";
import { Hint } from "../../types";
import { EditReportSectionDialog } from "./EditReportSectionDialog";
import { localizeReportSectionTitle } from "./localizeReportSectionTitle";
import {
  ReportSectionSources,
  ReportSectionWithSources,
  SourceReference,
} from "./ReportSectionSources";

type ReportSectionViewProps = {
  reportSection: ReportSection;
  accordionValue: string;
  terms: Hint[];
  sectionLinks?: SectionLink[];
  onRegenerateSection: (sectionId: string) => void;
  onEditSection: (sectionId: string, content: string) => Promise<void>;
  status?: ReportSectionStatus;
  templateName?: string | null;
  outputLanguage?: Language | null;
  isActionAvailable?: boolean;
  onSourceClick?: (source: SourceReference) => void;
  onSectionClick?: (sectionId: string, firstPageId: string) => void;
  batesLinks?: BatesLink[];
  onBatesClick?: (pageId: string) => void;
};

export const ReportSectionView = memo(
  ({
    reportSection,
    accordionValue,
    terms,
    sectionLinks = [],
    onRegenerateSection,
    onEditSection,
    status,
    templateName,
    outputLanguage,
    isActionAvailable = true,
    onSourceClick,
    batesLinks,
  }: ReportSectionViewProps) => {
    const { formatMessage } = useIntl();
    const sectionWithSources = reportSection as ReportSectionWithSources;
    const sources = (sectionWithSources.sources ?? []) as SourceReference[];
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const { ref: lazyRef, hasBeenVisible } = useLazyRender();

    const sectionId = reportSection.id ?? "";

    const handleRegenerate = useCallback(
      () => onRegenerateSection(sectionId),
      [onRegenerateSection, sectionId]
    );

    const handleEdit = useCallback(
      (content: string) => onEditSection(sectionId, content),
      [onEditSection, sectionId]
    );

    const isRegenerating = status === "processing" || status === "pending";
    const isSectionFailed = status === "failed";

    const displayTitle = localizeReportSectionTitle(
      reportSection.title,
      templateName,
      outputLanguage
    );

    const highlightedContent = useMemo(() => {
      if (!reportSection.content) return "";
      const preprocessed = ensureBoldHeadingSpacing(
        ensureMarkdownNewlines(
          unwrapAllCodeBlocks(normalizeLiteralNewlines(reportSection.content))
        )
      );
      return highlightTermsAndSectionsInMarkdown(
        preprocessed,
        terms,
        sectionLinks,
        batesLinks,
        reportSection.sourceMap
      );
    }, [
      reportSection.content,
      terms,
      sectionLinks,
      batesLinks,
      reportSection.sourceMap,
    ]);

    const renderSectionContent = () => {
      if (isRegenerating) {
        return (
          <div className="animate-pulse">
            <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full max-w-[330px] mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full max-w-[300px] mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full max-w-[260px] mb-2.5" />
          </div>
        );
      }

      if (isSectionFailed) {
        return (
          <Alert
            variant="default"
            className="border-red-200 bg-red-50 px-3 py-1.5"
          >
            <div className="flex items-center gap-2">
              <CircleAlert className="h-4 w-4 text-red-500 shrink-0" />
              <AlertDescription className="flex-1 text-red-800 text-xs">
                {formatMessage({ id: "chat.report.sectionFailed" })}
              </AlertDescription>
              {isActionAvailable && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="shrink-0 text-red-700 hover:bg-red-100 hover:text-red-900 gap-1"
                  onClick={handleRegenerate}
                >
                  <RefreshCw className="h-3 w-3" />
                  <FormattedMessage
                    id="report.rewrite"
                    defaultMessage="Rewrite"
                  />
                </Button>
              )}
            </div>
          </Alert>
        );
      }

      return (
        <div ref={lazyRef}>
          {hasBeenVisible ? (
            <>
              <Markdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
                className={TERMS_PROSE_CLASS}
                components={{
                  ...getTermsAndSectionsMarkdownComponents(
                    false, // isSummary
                    terms,
                    sectionLinks
                  ),
                }}
              >
                {highlightedContent}
              </Markdown>
              {sources.length > 0 && (
                <ReportSectionSources
                  sources={sources}
                  onSourceClick={onSourceClick}
                />
              )}
            </>
          ) : (
            <div className="animate-pulse">
              <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full max-w-[330px] mb-2.5" />
            </div>
          )}
        </div>
      );
    };

    return (
      <AccordionItem
        value={accordionValue}
        className="group border-none relative"
      >
        <div className="sticky top-0 z-10 bg-background py-1 -my-1">
          <AccordionTrigger className="py-0 hover:no-underline">
            <h2 className="text-sm font-semibold">{displayTitle}</h2>
          </AccordionTrigger>
        </div>
        <AccordionContent className="pb-0 pt-2">
          {renderSectionContent()}
        </AccordionContent>
        {!isRegenerating && !isSectionFailed && (
          <div className="h-8 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 mt-0">
            <div className="inline-flex items-center gap-2">
              <ContentCopyButton
                content={normalizeLiteralNewlines(reportSection.content ?? "")}
                preCopyMessageId="copy"
                color="muted-foreground"
                eventName="app/report/section_copy.button/click"
              />
              {isActionAvailable && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
                  onClick={() => {
                    trackEvent({
                      eventName: "app/report/section_edit.button/click",
                      properties: {
                        sectionId: reportSection.id,
                        sectionTitle: reportSection.title,
                      },
                    });
                    setEditDialogOpen(true);
                  }}
                  disabled={isRegenerating}
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  <FormattedMessage id="report.edit" defaultMessage="Edit" />
                </Button>
              )}
              {isActionAvailable && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
                  onClick={() => {
                    trackEvent({
                      eventName: "app/report/section_rewrite.button/click",
                      properties: {
                        sectionId: reportSection.id,
                        sectionTitle: reportSection.title,
                      },
                    });
                    handleRegenerate();
                  }}
                  disabled={isRegenerating}
                >
                  {isRegenerating ? (
                    <LoadingSpinner className="w-4 h-4 mr-1" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1" />
                  )}
                  <FormattedMessage
                    id="report.rewrite"
                    defaultMessage="Rewrite"
                  />
                </Button>
              )}
            </div>
          </div>
        )}

        <EditReportSectionDialog
          section={reportSection}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSave={handleEdit}
        />
      </AccordionItem>
    );
  }
);
